import React, { useCallback } from 'react';
import { Box, InputAdornment, InputBase, TextFieldProps, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole } from '@uplift/types';
import { AppointmentDurationMappingDurationMinutes } from '../../../../Calendar';
import { MobileSection } from '../../../../Form';
import { useDatePickerOpenedContext } from '../../../../Inputs';
import { AppointmentRepetitionSelect } from '../components/AppointmentRepetitionSelect.components';
import { AppointmentDateInput } from '../components/AppointmentDateInput.component';
import { StartTimeSelect } from '../components/StartTimeSelect';
import { UserInfo } from '../components/UserInfo';
import { InvitationAlert } from '../components/InvitationAlert.components';
import { useFormContext } from '../AddSessionForm.types';
import { SlotDurationInput } from '../components/SlotDurationInput.component';
import { ExistingOrInviteToggle } from '../components/ExistingOrInviteToggle.component';
import { NotesInput } from '../components/NotesInput.component';
import { AddSessionStatusAlert } from '../components/AddSessionStatusAlert.component';
import { useSessionFormFieldsSync } from '../components/form.hooks';
import { useAvailableSlots } from '../hooks';
import { AppointmentSlotDuration } from '@uplift/api';


export type AddSessionFormMobileProps = {
  open?: boolean;
  newEvent?: { start?: string };
  showInviteToggle?: boolean;
  showPatientPicker?: boolean;
};


const DateInputComponent: React.FC<TextFieldProps> = (props) => {
  const opened = useDatePickerOpenedContext();

  return (
    <InputBase
      inputRef={props.inputRef}
      value={props.value ?? '-'}
      onClick={props.onClick}
      onBlur={props.onBlur}
      style={{ height: 48, userSelect: 'none' }}
      inputProps={{ style: { textAlign: 'right' } }}
      endAdornment={(
        <InputAdornment position="start">
          {opened ? <ArrowDropUp color="action" /> : <ArrowDropDown color="action" />}
        </InputAdornment>
      )}
    />
  );
};

export const AddSessionFormMobile: React.VFC<AddSessionFormMobileProps> = ({ showInviteToggle = true, showPatientPicker = true, ...props }) => {
  const currentUser = useCurrentuser();
  const formikContext = useFormContext();
  const isPsychiatrist = currentUser.medicalRole === ProviderMedicalRole.psychiatrist;
  const isTherapist = currentUser.medicalRole === ProviderMedicalRole.therapist;

  const dateFormatted = React.useMemo(() => formikContext.values.date, [ formikContext.values.date ]);
  const appointmentDuration = React.useMemo(() => {
    if (formikContext.values.duration) {
      return AppointmentDurationMappingDurationMinutes[formikContext.values.duration];
    }

    return AppointmentDurationMappingDurationMinutes.sixty;
  }, [ formikContext.values.duration ]);
  const allAvailableSlots = useAvailableSlots(dateFormatted, appointmentDuration);

  useSessionFormFieldsSync({ open: props.open, newEvent: props.newEvent });

  const handleInviteToggleChange = useCallback((isNewPatient: boolean) => {
    if (!isPsychiatrist) {
      return;
    }

    if (isNewPatient === formikContext.values.isNewPatient) {
      return;
    }

    if (isNewPatient) {
      formikContext.setFieldValue('duration', AppointmentSlotDuration.sixty);
    } else {
      formikContext.setFieldValue('duration', AppointmentSlotDuration.thirty);
    }
  }, [ formikContext, isPsychiatrist ]);

  return (
    <Box display="flex" flexDirection="column">
      <Box px={2} pb={2}>
        <AddSessionStatusAlert />
      </Box>
      {(showInviteToggle || showPatientPicker) && (
        <Box px={2} pb={2} display="flex" flexDirection="column" gap="16px">
          {showInviteToggle && <ExistingOrInviteToggle name="isNewPatient" onChange={handleInviteToggleChange} />}

          <InvitationAlert />

          {showPatientPicker && <UserInfo />}
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap="16px">
        <MobileSection label="Date" divider>
          <AppointmentDateInput name="date" TextFieldComponent={DateInputComponent} />
        </MobileSection>

        <MobileSection label="Start time" divider>
          <StartTimeSelect
            data-testid="start-time-select"
            textOnly
            name="startTime"
            appointmentDuration={appointmentDuration}
            slots={allAvailableSlots}
          />
        </MobileSection>


        <MobileSection label="Duration" divider>
          <SlotDurationInput
            data-testid="duration-select"
            name="duration"
            startTime={formikContext.values.startTime}
            slots={allAvailableSlots}
            role={currentUser.medicalRole}
          />
        </MobileSection>

        {isTherapist && (
          <MobileSection label="Repeats" divider>
            <AppointmentRepetitionSelect name="repeats" data-testid="repetition-select" />
          </MobileSection>
        )}

        <MobileSection label="Location" divider>
          <Box display="flex" alignItems="center" style={{ cursor: 'default', paddingTop: '2px' }}>
            <Typography variant="body1">
            Virtual (Video and Audio)
            </Typography>
          </Box>
        </MobileSection>

        <MobileSection divider>
          <Box width="100%">
            <NotesInput name="notes" variant="mobile" color="secondary" />
          </Box>
        </MobileSection>
      </Box>
    </Box>
  );
};
